import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Transition, { TransitionStatus } from 'react-transition-group/Transition';
import { Carousel } from 'react-responsive-carousel';
import clsx from 'clsx';
import { useAccountSelectStore, useAccountsStore, useCurrentCardStore } from '@lib/store';
import { useAccountContext, useResponsive, useRouteHook } from '@shared/hooks';
import { PaletteUtils } from '@shared/utils';
import type { AccountEntity } from '@shared/api';
import { Item } from './item';
import { DropDownIcon } from '@shared/widgets';
import 'react-responsive-carousel/lib/styles/carousel.css';

const SCROLL_BY = 340;
const TRANSITION_TIME = 300;

export const getUrl = (account: AccountEntity) => {
  let result = `/card/${account.mainCard?.id}`;

  const hasStatements = window.location.pathname.includes('/statements');
  if (hasStatements) result += '/statements';

  return result;
};

interface AccountsSelectProps {
  className?: string;
  show?: boolean;
}

export const AccountsSelect = (props: AccountsSelectProps) => {
  const location = useLocation();
  const { navigate } = useRouteHook();

  const accountsStore = useAccountsStore();
  const currentCardStore = useCurrentCardStore();
  const accountSelectStore = useAccountSelectStore();

  const [hasScroll, setHasScroll] = useState<boolean>(false);
  const listRef = useRef<HTMLUListElement>(null);
  const transitionRef = useRef<HTMLElement>(null);

  const { account: currentAccount, card: currentCard } = useAccountContext();
  const { isMobile } = useResponsive();

  const transitionStatusClass = (state: TransitionStatus) => {
    switch (state) {
      case 'entering':
        return 'h-0 pb-0 pt-0 opacity-0';
      case 'entered':
        return 'h-[160px] pb-0 pt-[20px] mb-[20px] opacity-100';
      case 'exiting':
        return 'h-[160px] pb-0 pt-[20px] mb-[20px] opacity-100';
      case 'exited':
        return 'h-0 pb-0 pt-0 opacity-0';
      case 'unmounted':
        return '';
    }
  };

  const refreshScroll = useCallback(() => {
    if (!listRef.current) return;
    const result = listRef.current.offsetWidth < listRef.current.scrollWidth;
    if (result !== hasScroll) setHasScroll(result);
  }, [hasScroll]);

  useEffect(() => {
    refreshScroll();
  });

  useEffect(() => {
    currentCardStore.hideAllPanels();
    refreshScroll();
  }, [location.pathname]);

  useEffect(() => {
    if (currentCard?.id) currentCardStore.setCurrentCard(currentCard.id);
  }, [currentCard]);

  if (isMobile)
    return (
      <Carousel
        className={clsx(
          'transition-all ease-in-out duration-500 delay-150 overflow-hidden relative',
          'max-[840px]:max-w-[468px] max-[840px]:overflow-visible',
          props.className,
        )}
        selectedItem={currentAccount?.id ? accountsStore.data.findIndex((a) => a.id === currentAccount.id) : -1}
        showThumbs={false}
        showArrows={false}
        showStatus={false}
        onChange={(_, item) => {
          // @ts-ignore
          const account = item?.props?.account;
          if (!account) return;
          setTimeout(() => navigate(getUrl(account)), TRANSITION_TIME);
        }}
        transitionTime={TRANSITION_TIME}
      >
        {accountsStore.data.map((account) => (
          <Item key={account.id} account={account} card={currentAccount?.id === account.id ? currentCard : account.mainCard} />
        ))}
      </Carousel>
    );
  return (
    <Transition in={props.show && accountSelectStore.data} timeout={0} nodeRef={transitionRef}>
      {(state) => (
        <section
          className={clsx('transition-all ease duration-500 delay-100 overflow-hidden relative', transitionStatusClass(state), props.className)}
        >
          <ul
            ref={listRef}
            className={clsx(
              'border-none list-none flex pt-0 px-0 pb-[20px] m-0 overflow-x-scroll text-[14px] font-[500] scroll-smooth',
              PaletteUtils.isBordierPalette ? 'bg-[#f8f8f8]' : 'bg-[#1e1e21]',
            )}
          >
            {accountsStore.data.map((account, idx) => (
              <li
                key={idx}
                className={clsx(
                  'flex-[0_0_340px]',
                  PaletteUtils.isBordierPalette && 'border-r border-[#e1e1e1]',
                  account.id === currentAccount?.id && (PaletteUtils.isBordierPalette ? 'bg-[#dc291c]' : 'bg-[#323236]'),
                )}
                onClick={(currentCardStore.hideAllPanels, accountSelectStore.toggleAccountsSelect)}
              >
                <Item key={account.id} account={account} isActive={account.id === currentAccount?.id} />
              </li>
            ))}
          </ul>
          {hasScroll && (
            <div
              className='items-center cursor-pointer flex justify-between h-[140px] absolute top-5 w-12 z-2 bg-gradient-to-r from-black/70 to-black/0 absolute left-0'
              onClick={() => listRef.current?.scrollBy({ left: -SCROLL_BY, top: 0, behavior: 'smooth' })}
            >
              <div className='rotate-z-90'>
                <DropDownIcon />
              </div>
            </div>
          )}
          {hasScroll && (
            <div
              className='items-center cursor-pointer flex justify-center h-[140px] absolute top-5 w-12 z-2 right-0 bg-gradient-to-l from-black/70 to-black/1'
              onClick={() => listRef.current?.scrollBy({ left: SCROLL_BY, top: 0, behavior: 'smooth' })}
            >
              <div className='-rotate-z-90'>
                <DropDownIcon />
              </div>
            </div>
          )}
        </section>
      )}
    </Transition>
  );
};
